<template>
  <div>
    <template v-if="product">
      <div class="content">
        <page-title v-if="project === 'rzaska'" class="is-marginless">
          {{ product.description.name }}
        </page-title>

        <div v-if="project === 'schuette'" class="page-title is-marginless has-text-centered">
          <template v-if="splitTitle">
            <h1 class="title main-title is-marginless" style="display: inline;">
              {{ splitTitle.h1 }}
            </h1>
            <h2 style="display: inline; margin-top: 0px;">
              {{ splitTitle.h2 }}
            </h2>
          </template>
          <template v-else>
            <h1 class="title main-title has-text-centered">
              {{ product.description.name }}
            </h1>
          </template>
        </div>
        <div v-if="product && product.haveRate" class="field has-addons has-addons-centered m-t-10 rate-clickable-container">
          <a href="#comments" class="is-flex has-text-grey-dark" @click="scrollToComments"><b-rate class="rate-clickable" :value="product.offerAverageRate.rate" disabled /><span class="m-l-5">({{ product.offerAverageRate.count }})</span></a>
        </div>
        <div class="product-configurator-container">
          <div class="columns is-variable is-5">
            <div class="column is-6">
              <product-gallery v-if="getProductImages.length" :images="getProductImages" :name="product.product.description.name" />
            </div>
            <div class="column is-6">
              <product-filter :filters="filters" :loading-filter="loadingFilter" @selected="selected" />

              <template v-if="product && product.haveSizes">
                <product-sizes ref="productSizes" :size.sync="size" :product="product" @change="getPrices()" />
              </template>

              <product-addons :data="prices?.additionalOptions || null" :additional-options.sync="additionalOptions" @change="getPrices()" />

              <product-quantity :actual-quantity="quantity" :product="product" :max-slider-quantity="maxSliderQuantity" @quantity="q => quantity = q" />
              <product-price :prices="prices" :type-prices="product.typePrices" :price-per-item="checkAddProduct" />
              <product-shipping-info ref="shippingInfo" />

              <div class="product-prices-section">
                <b-button
                  :disabled="checkAddProduct"
                  :type="shoppingColor"
                  :class="['is-fullwidth m-t-10 shopping-button', {'shopping-button-empty-stock': !(product.actualStock === null || product.actualStock > 0)}]"
                  size="is-large"
                  icon-left="shopping-cart"
                  rounded
                  @click.stop="addCart()"
                >
                  <template v-if="product.actualStock === null || product.actualStock > 0">
                    <!-- <b-icon icon="shopping-cart" /> -->
                    {{ $t('products.add_to_cart') }}
                  </template>
                  <template v-else>
                    {{ $t('products.notAvailable') }}
                  </template>
                </b-button>

                <payments-icons class="product-payments" />
                <messenger-trigger
                  :id="product.id"
                  :title="product.description.name"
                  :label="{
                    type: ConversationTypes.PRODUCT, productId: String(product.id),
                    name: product.description.name,
                    image: product.mainImage.file.sizes.huge,
                    ['image-webp']: product.mainImage.file.sizes['huge-webp'],
                    link
                  }"
                  class="m-t-10"
                >
                  <b-button rounded class="is-fullwidth">
                    {{ $t('messenger.newTicket.product') }}
                  </b-button>
                </messenger-trigger>
              </div>

              <div v-if="checkIsSeller" class="mt-5 is-size-7">
                SKU: <b>{{ product.sku }}</b>
              </div>

              <product-group-deal v-if="product.activeGroupDeal" :data="product.activeGroupDeal.groupDeal" :offer-product="product.id" />
            </div>
          </div>
        </div>

        <div v-if="product.sizeStructure && product.displaySize" class="content m-t-40">
          <h2>{{ $t('fields.sizesTable') }}</h2>
          <div class="table-container">
            <table class="table is-bordered row-labels">
              <thead>
                <tr>
                  <th :style="{minWidth: 120 + 'px'}">
                    {{ $t('fields.height') }}/{{ $t('fields.width') }}
                  </th>
                  <th v-for="sizeCol in product.sizeStructure.sizes[0].sizes" :key="sizeCol.id" :style="{minWidth: 120 + 'px'}">
                    {{ sizeCol.from }} - {{ sizeCol.to }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="sizeRow in product.sizeStructure.sizes" :key="sizeRow.id">
                  <td>
                    {{ sizeRow.from }} - {{ sizeRow.to }}
                  </td>
                  <td v-for="sizeCol in sizeRow.sizes" :key="sizeCol.id">
                    {{ toCurrency(sizeCol.prices[0].brutto, null, sizeCol.prices[0].currency) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-if="!product.sizeStructure && product.priceWithQuantitative.length" class="content m-t-40">
          <!-- <h4> {{ $t('fields.quantitativePrice') }} </h4> -->
          <div class="table-container">
            <table class="table is-bordered is-marginless">
              <thead>
                <tr>
                  <th>{{ $t('fields.priceFrom') }}</th>
                  <th>{{ $t('fields.priceTo') }}</th>
                  <th>
                    {{ $t('fields.price') }}
                    <span class="subtitle is-7 has-text-weight-light is-marginless">
                      {{ product.typePrices === 'brutto' ? $t('fields.withVat') : $t('fields.withoutVat') }}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in product.priceWithQuantitative" :key="row.id">
                  <td>{{ row.from }}</td>
                  <td>{{ row.to }}</td>
                  <td>{{ toCurrency(row[product.typePrices], null, row.currency) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <product-bulletpoints :bullet-points="product.bulletPointsLang" />
        <product-specification :filters="filters" :product="product" :prices="prices" />
      </div>
      <content-generator :sections="product.sections" />
      <div class="content">
        <product-faqs :product="product" />
        <comments-from-product :offer-id="offerId" :initial-comments="initialComments" />
        <product-photos :product="product" />
        <additional-resources v-if="product?.gprs" :product="product.gprs" />
      </div>
    </template>
    <template v-else>
      <div class="content">
        <b-notification v-if="!loading" :closable="false" type="is-danger">
          <div class="columns is-mobile is-vcentered">
            <div class="column is-narrow">
              <b-icon size="is-large" icon="alert-circle" />
            </div>
            <div class="column">
              {{ $t('products.notFound') }}
            </div>
          </div>
        </b-notification>
      </div>
    </template>
  </div>
</template>

<script>
import MessengerTrigger from '../gotchi/MessengerTrigger'
import { ConversationTypes } from '../gotchi/config/ConversationTypes'
import ProductShippingInfo from './ProductShippingInfo'
import ProductSizes from './ProductSizes'
import ProductFilter from './ProductFilter'
import ProductSpecification from './ProductSpecification'
import ProductFaqs from './ProductFaqs'
import ProductBulletpoints from './ProductBulletpoints'
import ProductPhotos from './ProductPhotos'
import ProductAddons from './ProductAddons'
import AdditionalResources from './ProductAdditionalResources'
import toSlugTitle from '~/marketplace-front-core/utils/toSlugTitle'
import CommentsFromProduct from '~/marketplace-front-core/components/comments/CommentsFromProduct'
import ProductGallery from '~/marketplace-front-core/components/gallery/ZoomGallery'
import ProductPrice from '~/marketplace-front-core/components/products/ProductPrice'
import ProductGroupDeal from '~/marketplace-front-core/components/products/ProductGroupDeal'
import ProductQuantity from '~/marketplace-front-core/components/products/ProductQuantity'
import product from '~/marketplace-front-core/mixin/product'
import PaymentsIcons from '~/marketplace-front-core/components/shared/PaymentsIcons'
import ContentGenerator from '~/marketplace-front-core/components/contentEditorClient/content.vue'

export default {
  name: 'ComponentProduct',
  components: {
    ProductFilter,
    ProductSizes,
    ProductShippingInfo,
    CommentsFromProduct,
    ProductGallery,
    ProductQuantity,
    ProductGroupDeal,
    ProductPrice,
    ProductSpecification,
    ProductBulletpoints,
    ProductFaqs,
    ProductAddons,
    ContentGenerator,
    ProductPhotos,
    PaymentsIcons,
    AdditionalResources,
    MessengerTrigger
  },
  mixins: [product],
  props: {
    getProduct: {
      type: Object,
      default: null
    },
    initialComments: {
      type: Object,
      default: null
    },
    maxSliderQuantity: {
      type: [Boolean, Number],
      default: false
    },
    redirectToCheckout: {
      type: Boolean
    }
  },
  data () {
    return {
      offerId: null,
      shoppingColor: process.env.shoppingColor,
      additionalOptions: []
    }
  },
  head () {
    const i18nHead = this.$nuxtI18nHead({ addSeoAttributes: true })
    const frontBaseUrl = process.env.frontBaseUrl
    const mainLang = process.env.lang
    const actualLanguage = this.$store.state.i18n.locale
    const availableLinks = this.links ? Object.keys(this.links) : []

    const getNameFromLang = (lang) => {
      if (!availableLinks.length) {
        return 'product'
      } else if (this.links[lang]) {
        return this.links[lang]
      } else if (this.links[mainLang]) {
        return this.links[mainLang]
      } else {
        return this.links[availableLinks[0]]
      }
    }

    const links = this._clone(i18nHead.link)

    links.forEach((element) => {
      if (element.hid === 'i18n-alt-' + element.hreflang) {
        element.href = frontBaseUrl + this.localePath({ name: 'product', params: { productName: getNameFromLang(element.hreflang.toLowerCase()), productId: this.$route.params.productId } }, element.hreflang.toLowerCase())
      }
      if (element.hid === 'i18n-xd') {
        element.href = frontBaseUrl + this.localePath({ name: 'product', params: { productName: getNameFromLang(mainLang), productId: this.$route.params.productId } }, mainLang)
      }
      if (element.hid === 'i18n-can') {
        if (this.product && this.product.mainOP && this.product.mainOP.name && this.product.mainOP.id) {
          element.href = frontBaseUrl + this.localePath({ name: 'product', params: { productName: this.product.mainOP.link, productId: this.product.mainOP.id } })
        } else {
          element.href = frontBaseUrl + this.localePath({ name: 'product', params: { productName: getNameFromLang(actualLanguage), productId: this.$route.params.productId } })
        }
      }
    })

    const canonicalLink = links.find(link => link.hid === 'i18n-can')

    return {
      titleTemplate: '%s',
      title: this.product ? this.product.description.nameSeo || this.product.description.name : this.$t('fields.product'),
      htmlAttrs: {
        ...i18nHead.htmlAttrs
      },
      meta: [
        ...i18nHead.meta,
        ...(this.product && this.product.description ? [{ hid: 'description', name: 'description', content: this.product.description.descriptionSeo || this.product.description.name || this.$t('fields.product') }] : []),
        ...(this.product ? this.getOpenGraph : []),
        ...(this.product && this.product.followIndex === false ? [{ name: 'robots', content: 'noindex,nofollow' }] : []),

        ...(this.product ? [{ property: 'og:type', content: 'website' }] : []),
        ...(this.product ? [{ property: 'og:title', content: this.product.description.nameSeo || this.product.description.name || this.$t('fields.product') }] : []),
        ...(this.product ? [{ property: 'og:description', content: this.product.description.descriptionSeo || this.product.description.name || this.$t('fields.product') }] : []),
        ...(canonicalLink?.href ? [{ property: 'og:url', content: canonicalLink?.href }] : []),
        ...(this.product ? [{ property: 'og:image', content: this.product?.mainImage?.file?.sizes?.superaHuge }] : []),
        ...(this.product ? [{ property: 'og:image:alt', content: this.product.description.name }] : [])

      ],
      link: [
        ...this.metaLink(links)
      ],
      script: [
        ...(this.product ? [{ type: 'application/ld+json', json: this.getProductSchemaOrg }] : []),
        ...(this.product && this.product.faqsLang && this.product.faqsLang.length ? [{ type: 'application/ld+json', json: this.getFaqSchemaOrg }] : [])
      ]
    }
  },
  computed: {
    link () {
      return `${process.env.frontBaseUrl}/resolve/product/${this.product.id}`
    },
    ConversationTypes () {
      return ConversationTypes
    },
    getProductImages () {
      if (this.product && this.product.filePivots && this.product.filePivots.length) {
        return this.product.filePivots.filter(x => x.file.sizes)
      }

      return []
    },
    getProductSchemaOrg () {
      const schema = {
        '@context': 'https://schema.org/',
        '@type': 'Product',
        name: this.product.description.nameSeo || this.product.description.name,
        offers: {
          '@type': 'Offer',
          url: process.env.frontBaseUrl + this.$route.fullPath,
          priceCurrency: this.prices.currency,
          price: this.prices.price,
          availability: 'https://schema.org/InStock',
          itemCondition: 'https://schema.org/NewCondition'
        }
      }

      if (process.env.project === 'schuette') {
        const currentLocale = this.$store.state.i18n.locale

        schema.offers.shippingDetails = {
          '@type': 'OfferShippingDetails',
          shippingRate: {
            '@type': 'MonetaryAmount',
            value: 0,
            currency: this.prices.currency
          },
          shippingDestination: {
            '@type': 'DefinedRegion',
            addressCountry: 'EU'
          },
          deliveryTime: {
            '@type': 'ShippingDeliveryTime',
            handlingTime: {
              '@type': 'QuantitativeValue',
              minValue: 1,
              maxValue: 3,
              unitCode: 'd'
            },
            transitTime: {
              '@type': 'QuantitativeValue',
              minValue: 2,
              maxValue: 3,
              unitCode: 'd'
            }
          }
        }

        const returnLinks = {
          en: 'https://schuette.shop/en/5645354',
          nl: 'https://schuette.shop/nl/435223',
          no: 'https://schuette.shop/no/435224',
          fr: 'https://schuette.shop/fr/53412645',
          es: 'https://schuette.shop/es/7455455',
          it: 'https://schuette.shop/it/267843',
          de: 'https://schuette.shop/435222'
        }

        schema.offers.hasMerchantReturnPolicy = {
          '@type': 'MerchantReturnPolicy',
          applicableCountry: 'EU',
          returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
          merchantReturnDays: 14,
          merchantReturnLink: returnLinks[currentLocale] || returnLinks.de,
          refundType: 'https://schema.org/FullRefund',
          returnMethod: 'https://schema.org/ReturnByMail',
          returnFees: 'https://schema.org/ReturnFeesCustomerResponsibility',
          ItemCondition: 'https://schema.org/NewCondition'
        }

        const brandNames = {
          en: 'Schuette® Manufacture',
          nl: 'Schuette® Manufactuur',
          no: 'Schuette® Manufaktur',
          fr: 'Manufacture Schuette®',
          es: 'Manufactura Schuette®',
          it: 'Manifattura Schuette®',
          de: 'Schuette® Manufaktur'
        }

        schema.brand = { '@type': 'Brand', name: brandNames[currentLocale] || brandNames.de }
      }

      if (this.product.description) { schema.description = this.product.description.descriptionSeo || this.product.description.name || this.$t('fields.product') }
      if (this.product.mainImage) { schema.image = this.product?.mainImage?.file?.sizes?.superaHuge }
      if (this.product.sku) { schema.sku = this.product.sku }
      if (this.product.ean) { schema.gtin = this.product.ean }
      // if (this.getTypeGtin()) { schema[this.getTypeGtin()] = this.product.ean }
      if (this.product.offerAverageRate.count) {
        schema.aggregateRating = {
          '@type': 'AggregateRating',
          ratingValue: this.product.offerAverageRate.rate,
          ratingCount: this.product.offerAverageRate.count
        }
      }
      if (this.product.featuredReviewsList && this.product.featuredReviewsList.length) {
        schema.review = this.product.featuredReviewsList.map((review) => {
          return {
            '@type': 'UserReview',
            reviewRating: {
              '@type': 'Rating',
              ratingValue: review.star
            },
            author: {
              '@type': 'Person',
              name: review.user?.displayName || 'Anonymous'
            },
            name: this.$t('products.verifiedPurchase'),
            reviewBody: review.comment,
            datePublished: review.createdAt ? this.$moment(review.createdAt).format('YYYY-MM-DD') : undefined
          }
        })
      }
      return schema
    },
    getFaqSchemaOrg () {
      const entity = this.product.faqsLang.map((faq) => {
        return {
          '@type': 'Question',
          name: faq.question.replace(/["]+/g, '&quot;'),
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer.replace(/["]+/g, '&quot;')
          }
        }
      })
      const schema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [...entity]
      }
      return schema
    },
    splitTitle () {
      const name = this.product?.description?.name || ''
      const index = name.indexOf('!')
      if (index !== -1) {
        const h1 = name.substring(0, index).trim()
        const h2 = name.substring(index + 1).trim()
        if (h1 && h2) {
          return { h1, h2 }
        }
      }
      return null
    },
    getOpenGraph () {
      const schema = [
        { hid: 'og:type', property: 'og:type', content: 'product' },
        { hid: 'og:url', property: 'og:url', content: process.env.frontBaseUrl + this.$route.fullPath }
      ]
      if (this.product.description) {
        schema.push({ hid: 'og:title', property: 'og:title', content: this.product.description.nameSeo || this.product.description.name || this.$t('fields.product') })
        schema.push({ hid: 'og:description', property: 'og:description', content: this.product.description.descriptionSeo || this.product.description.name || this.$t('fields.product') })
      }
      if (this.product.mainImage) {
        schema.push({ hid: 'og:image', property: 'og:image', content: this.product?.mainImage?.file?.sizes?.superaHuge })
      }

      // warunek content dla schuette i rzaska nowe
      const brand =
       process.env.project === 'schuette'
         ? 'Schuette® Manufaktur'
         : process.env.project === 'rzaska'
           ? 'Rząska® Manufaktur'
           : null

      if (brand) {
        schema.push({
          hid: 'product:brand',
          property: 'product:brand',
          content: brand
        })
      }

      const availability = (this.product.actualStock === 0)
        ? 'out of stock'
        : 'in stock'

      schema.push({ hid: 'product:availability', property: 'product:availability', content: availability })
      schema.push({ hid: 'product:condition', property: 'product:condition', content: 'new' })

      if (this.prices) {
        schema.push({ hid: 'product:price:amount', property: 'product:price:amount', content: this.prices.price })
        schema.push({ hid: 'product:price:currency', property: 'product:price:currency', content: this.prices.currency })
      }
      return schema
    }
  },
  watch: {
    '$route.params.productId' (productId) {
      this.changeProduct(productId, null, false)
    }
  },
  mounted () {
    if (this.$store.state.page.productScroll) {
      window.scrollTo(0, 0)
      this.$store.dispatch('page/productScroll', false)
    }

    if (this.getProduct && this.$refs.shippingInfo) {
      this.$refs.shippingInfo.set(false)
      this.$refs.shippingInfo.get(this.product.id, this.initialQuantity)
    }
  },
  created () {
    if (this.getProduct) {
      this.setData(this.getProduct)
    }
    this.loading = false
  },
  methods: {
    addCartApi () {
      return this.$api.cart.add(this.product.id, {
        quantity: this.quantity,
        size: this.product?.haveSizes ? this.size : null,
        additionalOptions: this.additionalOptions
      })
    },
    scrollToComments () {
      const element = document.getElementById('comments')
      if (element) {
        const top = element.getBoundingClientRect().top
        window.scrollTo({ top, behavior: 'smooth' })
      }
    },
    selected (productId, optionId) {
      if (!this.disableChangeProduct) {
        this.lastClickFilter = { productId, optionId }
        this.changeProduct(productId, optionId)
      }
    },
    changeProduct (productId, optionId, setdataOption = true) {
      const switchProductId = this._clone(Number(productId))
      const actualProductId = this.product && this.product.id ? Number(this.product.id) : null

      if (switchProductId !== actualProductId && switchProductId !== this.getingProduct) {
        if (optionId) {
          this.lastClickFilter = { productId: switchProductId, optionId }
        }
        this.startChangeProduct(switchProductId)

        const query = this._clone(this.$route.query)
        const params = this._clone(this.$route.params)
        const isProductSizesPage = /^productSizes___/.test(this.$route.name)

        if (isProductSizesPage) {
          delete params.width
          delete params.height
          delete params.type
          params.productId = switchProductId

          return this.$router.push(this.localePath({ name: 'product', params, query }))
        }

        const checkAdditionalOptions = this.prices?.additionalOptions && this.prices.additionalOptions.length
        this.$api.default.getProductCT(switchProductId, { sizes: this.size, selectedOptions: checkAdditionalOptions ? this.additionalOptions : null }, this.cancelToken.token)
          .then((res) => {
            if (res && res.offerProduct.description.name) {
              this.finishChangeProduct(switchProductId)
              this.setData(res, setdataOption)
            }
          })
          .catch(() => {
            this.resetData()
            this.finishChangeProduct(switchProductId)
          })
      }
    },
    setData (res, changeLink) {
      this.offerId = res.id
      this.setDataGlobal(res)

      const query = this._clone(this.$route.query)
      const productName = this.product.link
      const params = this._clone(this.$route.params)

      if (changeLink) {
        this.$router.push({ params: { productName, productId: this.product.id }, query })
      } else if (params.productName !== productName || Number(params.productId) !== this.product.id) {
        this.$router.replace({ params: { productName, productId: this.product.id }, query }).catch(() => {})
      }

      this.setDataGlobalFinish(res)
    },
    resetData () {
      this.resetDataGlobal()
      this.offerId = null
    },
    getTypeGtin () {
      const gtins = [8, 12, 13, 14]
      if (this.product && this.product.ean && gtins.includes(this.product.ean.length)) {
        return 'gtin' + this.product.ean
      } else {
        return false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .checkout-payments {
    margin-top: 16px;
    flex-wrap: wrap;
    justify-content: flex-start;
  }
</style>
